import { Fragment, useMemo, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { Listbox, Transition } from '@headlessui/react';
import { usePopper } from 'react-popper';
import classNames from 'classnames';

import TriangleIcon from '@crazyegginc/hatch/dist/images/icon-triangle-filled.svg?react';
import TickIcon from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg?react';

function dummy(children) {
  return children;
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  label,
  labelClassName,
  buttonClassName,
  position = 'bottom',
  popupClassName,
  popperClassName,
  size = 'base',
  renderInPortal = false,
  container = 'body',
  error = '',
  ...props
}) {
  const errorId = `select-error-${useId()}`;

  const getDisplayLabel = (option) => {
    return option?.label ?? option?.value;
  };

  const popperOptions = {
    placement: 'bottom',

    modifiers: [
      useMemo(
        () => ({
          name: 'widthModifier',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
          },
          effect: ({ state }) => {
            state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
          },
        }),
        [],
      ),
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: 'bottom',
          fallbackPlacements: 'bottom',
        },
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false,
        },
      },

      useMemo(
        () => ({
          name: 'offset',
          options: {
            offset: () => {
              return [0, 4];
            },
          },
        }),
        [],
      ),
    ],
  };

  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, popperOptions);
  const func = renderInPortal ? createPortal : dummy;
  const maybePopperAttributes = renderInPortal ? attributes.popper : [];

  const buttonLabel = getDisplayLabel(options.find((x) => x.value === value));
  const showPlaceHolder = !buttonLabel;

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <div className="relative text-sm">
          {!!label && (
            <Listbox.Label className={classNames('text-gray-200 mb-1.25 leading-none block', labelClassName)}>
              {label}
            </Listbox.Label>
          )}
          <div ref={setReferenceElement}>
            <Listbox.Button
              aria-describedby={error ? errorId : null}
              aria-invalid={!!error}
              className={classNames(
                'relative w-full rounded-md pl-3 pr-8 focus:outline-none',
                'text-left leading-none border',
                'focus-visible:outline-white focus-visible:outline-dashed bg-gray-500',
                buttonClassName,
                {
                  'h-[30px]': size === 'base',
                  'h-[37px]': size === 'lg',
                  'text-white': !disabled && !showPlaceHolder,
                  'text-white/30': disabled && !showPlaceHolder,
                  'text-gray-300': showPlaceHolder,
                  'cursor-not-allowed': disabled,
                  'cursor-pointer': !disabled,
                  'border-red-500': !!error,
                  'border-transparent': !error,
                },
              )}
              {...props}
            >
              <span className="block truncate">{buttonLabel ?? placeholder}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                <TriangleIcon className="h-2 w-2 fill-current text-white/50" />
              </span>
            </Listbox.Button>
          </div>
          <Transition
            appear={true}
            show={open}
            as={renderInPortal ? 'div' : Fragment}
            leave="transition-opacity ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            enter="transition-opacity ease-in duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
          >
            {func(
              <div
                ref={setPopperElement}
                className={classNames('z-10', popperClassName, {
                  'absolute mt-1 w-full': !renderInPortal,
                })}
                style={renderInPortal ? { ...styles.popper } : {}}
                {...maybePopperAttributes}
              >
                <Listbox.Options
                  static
                  className={classNames(
                    'rounded p-1.25 focus:outline-none w-full',
                    'max-h-[300px] !overflow-y-auto styled-scrollbar',
                    'bg-gray-600 shadow-md text-white text-sm',
                    popupClassName,
                    {
                      'mt-1': position === 'bottom',
                      'bottom-full mb-1': position === 'top',
                    },
                  )}
                >
                  {options
                    .filter((x) => x.value !== null)
                    .map((option) => (
                      <Listbox.Option
                        key={option.value}
                        className={({ active }) =>
                          classNames('relative flex min-h-[25px] select-none items-center py-1 pl-7 pr-3', {
                            'bg-blue-500 rounded cursor-pointer': active,
                            'text-white/30 cursor-not-allowed': option.disabled,
                          })
                        }
                        value={option.value}
                        disabled={option.disabled}
                      >
                        {({ selected }) => (
                          <>
                            <span className="block grow">{getDisplayLabel(option)}</span>
                            {selected ? (
                              <span className="absolute left-0 top-0 flex h-[25px] items-center pl-2.5">
                                <TickIcon className="h-2.5 w-2.5 fill-current" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                </Listbox.Options>
              </div>,
              document.querySelector(container),
            )}
          </Transition>
          {!!error && typeof error === 'string' && (
            <div className="relative h-5 mt-0.5" id={errorId}>
              <div className="text-red-500 text-xs absolute whitespace-nowrap">{error}</div>
            </div>
          )}
          {!!error && typeof error !== 'string' && typeof error !== 'boolean' && (
            <div className="text-red-500 text-xs min-h-[20px] translate-y-0.5" id={errorId}>
              {error}
            </div>
          )}
        </div>
      )}
    </Listbox>
  );
}
